import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import {
  emailMarketingApiService,
  ApiCampaign,
  ApiConfig,
  ApiListSetting,
  ApiOverview,
  ApiSubscriber,
  ApiTemplate,
  ApiCustomField,
  ApiListSegment,
  ApiListTag,
  ApiMarketingList,
  ApiPhysicalAddressOptions,
} from "./services/api";

export function useEmailMarketingCore() {
  const intl = useIntl();
  const [overview, setOverview] = useState<ApiOverview | null>(null);
  const [config, setConfig] = useState<ApiConfig | null>(null);
  const [listSetting, setListSetting] = useState<ApiListSetting | null>(null);
  const [subscribers, setSubscribers] = useState<ApiSubscriber[]>([]);
  const [campaigns, setCampaigns] = useState<ApiCampaign[]>([]);
  const [templates, setTemplates] = useState<ApiTemplate[]>([]);
  const [suppressions, setSuppressions] = useState<
    { email: string; reason: string; date: string }[]
  >([]);
  const [customFields, setCustomFields] = useState<ApiCustomField[]>([]);
  const [listSegments, setListSegments] = useState<ApiListSegment[]>([]);
  const [listTags, setListTags] = useState<ApiListTag[]>([]);
  const [marketingLists, setMarketingLists] = useState<ApiMarketingList[]>([]);
  const [defaultListId, setDefaultListId] = useState<string | null>(null);
  const [physicalAddressOptions, setPhysicalAddressOptions] =
    useState<ApiPhysicalAddressOptions | null>(null);
  const [reports, setReports] = useState<{
    openSeries: number[];
    openRows: { time: string; broadcasts: number; workflows: number; unique: number; total: number }[];
    campaigns: ApiCampaign[];
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const loadOverview = useCallback(async (companyId: string) => {
    setLoading(true);
    try {
      const data = await emailMarketingApiService.getOverview(companyId);
      setOverview(data);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "app.toast.error13" }));
    } finally {
      setLoading(false);
    }
  }, [intl]);

  const loadConfig = useCallback(async (companyId: string) => {
    setLoading(true);
    try {
      const data = await emailMarketingApiService.getConfig(companyId);
      setConfig(data);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "app.toast.error13" }));
    } finally {
      setLoading(false);
    }
  }, [intl]);

  const loadList = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getList(companyId);
      setListSetting(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadSubscribers = useCallback(async (companyId: string, listId?: string) => {
    try {
      const data = await emailMarketingApiService.getSubscribers(companyId, listId);
      setSubscribers(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadMarketingLists = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getMarketingLists(companyId);
      setMarketingLists(data.lists);
      setDefaultListId(data.defaultListId);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const createMarketingList = useCallback(
    async (companyId: string, body: { name: string; description?: string }) => {
      try {
        const list = await emailMarketingApiService.createMarketingList(companyId, body);
        setMarketingLists((current) => [...current, list]);
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.lists.created" })
        );
        await loadOverview(companyId);
        return list;
      } catch (error: any) {
        console.error(error);
        const message =
          error?.response?.data?.message ||
          intl.formatMessage({ id: "app.emailMarketing.lists.createFailed" });
        toast.error(message);
        return null;
      }
    },
    [intl, loadOverview]
  );

  const loadCampaigns = useCallback(async (companyId: string) => {
    setLoading(true);
    try {
      const data = await emailMarketingApiService.getCampaigns(companyId);
      setCampaigns(data);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "app.toast.error13" }));
    } finally {
      setLoading(false);
    }
  }, [intl]);

  const loadTemplates = useCallback(async (companyId: string) => {
    setLoading(true);
    try {
      const data = await emailMarketingApiService.getTemplates(companyId);
      setTemplates(data);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "app.toast.error13" }));
    } finally {
      setLoading(false);
    }
  }, [intl]);

  const getTemplate = useCallback(
    async (companyId: string, templateId: string) => {
      try {
        return await emailMarketingApiService.getTemplate(companyId, templateId);
      } catch (error) {
        console.error(error);
        toast.error(
          intl.formatMessage({ id: "app.emailMarketing.templates.loadFailed" })
        );
        return null;
      }
    },
    [intl]
  );

  const createTemplate = useCallback(
    async (
      companyId: string,
      body: { name?: string; subject?: string; html?: string; editor?: string }
    ) => {
      try {
        const template = await emailMarketingApiService.createTemplate(
          companyId,
          body
        );
        setTemplates((current) => [template, ...current]);
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.templates.created" })
        );
        return template;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const loadSuppressions = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getSuppressions(companyId);
      setSuppressions(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadReports = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getReportsOverview(companyId);
      setReports(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const importSubscribers = useCallback(
    async (
      companyId: string,
      body: { contactIds?: number[]; sources?: string[] } = {}
    ) => {
      try {
        const result = await emailMarketingApiService.importSubscribers(
          companyId,
          body
        );
        toast.success(
          intl.formatMessage(
            { id: "app.emailMarketing.api.imported" },
            { count: result.imported }
          )
        );
        await loadSubscribers(companyId);
        await loadOverview(companyId);
        return result;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.emailMarketing.api.importFailed" }));
        return null;
      }
    },
    [intl, loadSubscribers, loadOverview]
  );

  const deleteSubscriber = useCallback(
    async (companyId: string, email: string, listId?: string) => {
      try {
        await emailMarketingApiService.deleteSubscriber(companyId, email, listId);
        await loadSubscribers(companyId, listId);
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.api.subscriberDeleted" })
        );
        await loadOverview(companyId);
        return true;
      } catch (error: any) {
        console.error(error);
        const message =
          error?.response?.data?.message ||
          intl.formatMessage({ id: "app.emailMarketing.api.subscriberDeleteFailed" });
        toast.error(message);
        return false;
      }
    },
    [intl, loadOverview, loadSubscribers]
  );

  const importSubscribersBulk = useCallback(
    async (
      companyId: string,
      body: { paste?: string; contacts?: { email: string; name?: string }[] }
    ) => {
      try {
        const result = await emailMarketingApiService.importSubscribersBulk(
          companyId,
          body
        );
        toast.success(
          intl.formatMessage(
            { id: "app.emailMarketing.api.importBulkResult" },
            {
              imported: result.imported,
              created: result.created,
              skipped: result.skipped,
            }
          )
        );
        await loadSubscribers(companyId);
        await loadOverview(companyId);
        return result;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.emailMarketing.api.importFailed" }));
        return null;
      }
    },
    [intl, loadSubscribers, loadOverview]
  );

  const createSubscriber = useCallback(
    async (companyId: string, body: { email: string; name?: string }) => {
      try {
        const subscriber = await emailMarketingApiService.createSubscriber(
          companyId,
          body
        );
        setSubscribers((current) => {
          const exists = current.some((item) => item.email === subscriber.email);
          if (exists) {
            return current.map((item) =>
              item.email === subscriber.email ? subscriber : item
            );
          }
          return [subscriber, ...current];
        });
        toast.success(
          intl.formatMessage(
            { id: subscriber.created ? "app.emailMarketing.api.subscriberCreated" : "app.emailMarketing.api.subscriberUpdated" }
          )
        );
        await loadOverview(companyId);
        return subscriber;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.emailMarketing.api.importFailed" }));
        return null;
      }
    },
    [intl, loadOverview]
  );

  const createCampaign = useCallback(
    async (
      companyId: string,
      body: {
        subject?: string;
        audience?: string;
        createType?: string;
        html?: string;
        sendgridListId?: string;
        listId?: string;
      }
    ) => {
      try {
        const campaign = await emailMarketingApiService.createCampaign(companyId, body);
        setCampaigns((current) => [campaign, ...current]);
        toast.success(intl.formatMessage({ id: "app.emailMarketing.mock.draftCreated" }));
        return campaign;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const updateCampaign = useCallback(
    async (
      companyId: string,
      campaignId: string,
      body: {
        subject?: string;
        audience?: string;
        html?: string;
        sendgridListId?: string;
        listId?: string;
      }
    ) => {
      try {
        const campaign = await emailMarketingApiService.updateCampaign(
          companyId,
          campaignId,
          body
        );
        setCampaigns((current) =>
          current.map((item) => (item.id === campaign.id ? campaign : item))
        );
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.draftSaved" }));
        return campaign;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const deleteCampaign = useCallback(
    async (companyId: string, campaignId: string) => {
      try {
        await emailMarketingApiService.deleteCampaign(companyId, campaignId);
        setCampaigns((current) => current.filter((item) => item.id !== campaignId));
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.draftDeleted" }));
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
      }
    },
    [intl]
  );

  const deleteCampaignsBulk = useCallback(
    async (companyId: string, campaignIds: string[]) => {
      if (campaignIds.length === 0) {
        return false;
      }
      try {
        await Promise.all(
          campaignIds.map((campaignId) =>
            emailMarketingApiService.deleteCampaign(companyId, campaignId)
          )
        );
        setCampaigns((current) =>
          current.filter((item) => !campaignIds.includes(item.id))
        );
        toast.success(
          intl.formatMessage(
            { id: "app.emailMarketing.api.bulkDeleted" },
            { count: campaignIds.length }
          )
        );
        return true;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return false;
      }
    },
    [intl]
  );

  const cancelScheduleCampaign = useCallback(
    async (companyId: string, campaignId: string) => {
      try {
        const campaign = await emailMarketingApiService.cancelScheduleCampaign(
          companyId,
          campaignId
        );
        setCampaigns((current) =>
          current.map((item) => (item.id === campaign.id ? campaign : item))
        );
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.api.scheduleCancelled" })
        );
        return campaign;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const getCampaign = useCallback(async (companyId: string, campaignId: string) => {
    try {
      return await emailMarketingApiService.getCampaign(companyId, campaignId);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "app.toast.error13" }));
      return null;
    }
  }, [intl]);

  const sendTest = useCallback(
    async (
      companyId: string,
      campaignId: string,
      email: string,
      html?: string,
      options?: { fromEmail?: string; fromName?: string }
    ) => {
      try {
        await emailMarketingApiService.sendTest(companyId, campaignId, {
          email,
          html,
          fromEmail: options?.fromEmail,
          fromName: options?.fromName,
        });
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.testSent" }));
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
      }
    },
    [intl]
  );

  const scheduleCampaign = useCallback(
    async (companyId: string, campaignId: string, sendAt?: string) => {
      try {
        const campaign = await emailMarketingApiService.scheduleCampaign(
          companyId,
          campaignId,
          sendAt ? { sendAt } : undefined
        );
        setCampaigns((current) =>
          current.map((item) => (item.id === campaign.id ? campaign : item))
        );
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.scheduled" }));
        return campaign;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const sendCampaignNow = useCallback(
    async (
      companyId: string,
      campaignId: string,
      options?: {
        notifyEmail?: string;
        html?: string;
        fromEmail?: string;
        fromName?: string;
      }
    ) => {
      try {
        const campaign = await emailMarketingApiService.sendCampaignNow(
          companyId,
          campaignId,
          options
        );
        setCampaigns((current) =>
          current.map((item) => (item.id === campaign.id ? campaign : item))
        );
        toast.success(
          intl.formatMessage(
            { id: "app.emailMarketing.api.sent" },
            { count: campaign.sentTo || 1 }
          )
        );
        return campaign;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const updateList = useCallback(
    async (
      companyId: string,
      body: {
        listId?: string;
        listName?: string;
        description?: string;
        fromEmail?: string;
        fromName?: string;
        physicalAddress?: ApiListSetting["physicalAddress"];
      }
    ) => {
      try {
        const list = await emailMarketingApiService.updateList(companyId, body);
        setListSetting(list);
        if (body.fromEmail) {
          setConfig((current) =>
            current
              ? {
                  ...current,
                  activeSender: {
                    email: list.fromEmail || body.fromEmail || "",
                    name: list.fromName || body.fromName || list.fromEmail || "",
                    verified: current.senders.some(
                      (sender) =>
                        sender.email === (list.fromEmail || body.fromEmail) &&
                        sender.verified
                    ),
                  },
                }
              : current
          );
        }
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.listSaved" }));
        return list;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const createSender = useCallback(
    async (
      companyId: string,
      body: { email: string; name?: string }
    ) => {
      try {
        const sender = await emailMarketingApiService.createSender(companyId, body);
        setConfig((current) =>
          current
            ? {
                ...current,
                senders: [...current.senders, sender],
                activeSender: current.activeSender || {
                  email: sender.email,
                  name: sender.name,
                  verified: sender.verified,
                },
              }
            : current
        );
        if (!listSetting?.fromEmail) {
          await emailMarketingApiService.updateList(companyId, {
            fromEmail: sender.email,
            fromName: sender.name,
          });
          await loadList(companyId);
        }
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.senderAdded" }));
        return sender;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl, listSetting?.fromEmail, loadList]
  );

  const createUnsubscribeGroup = useCallback(
    async (
      companyId: string,
      body: { name: string; description?: string }
    ) => {
      try {
        const group = await emailMarketingApiService.createUnsubscribeGroup(
          companyId,
          body
        );
        setConfig((current) =>
          current
            ? {
                ...current,
                unsubscribeGroups: [...current.unsubscribeGroups, group],
              }
            : current
        );
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.groupAdded" }));
        return group;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const loadDomainDns = useCallback(async (companyId: string) => {
    try {
      return await emailMarketingApiService.getDomainDns(companyId);
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "app.toast.error13" }));
      return null;
    }
  }, [intl]);

  const loadCustomFields = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getCustomFields(companyId);
      setCustomFields(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadListSegments = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getListSegments(companyId);
      setListSegments(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadListTags = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getListTags(companyId);
      setListTags(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const createListTag = useCallback(
    async (
      companyId: string,
      body: { name: string; textColor?: string; backgroundColor?: string }
    ) => {
      try {
        const tag = await emailMarketingApiService.createListTag(companyId, body);
        setListTags((current) => [...current, tag].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.tagCreated" }));
        return tag;
      } catch (error: any) {
        console.error(error);
        const message =
          error?.response?.data?.message ||
          intl.formatMessage({ id: "app.emailMarketing.api.tagCreateFailed" });
        toast.error(message);
        return null;
      }
    },
    [intl]
  );

  const loadPhysicalAddressOptions = useCallback(async (companyId: string) => {
    try {
      const data = await emailMarketingApiService.getPhysicalAddressOptions(companyId);
      setPhysicalAddressOptions(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const createCustomField = useCallback(
    async (
      companyId: string,
      body: { name: string; type?: string; subscriberCanUpdate?: boolean }
    ) => {
      try {
        const field = await emailMarketingApiService.createCustomField(companyId, body);
        setCustomFields((current) => [...current, field]);
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.fieldAdded" }));
        return field;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const createListSegment = useCallback(
    async (companyId: string, body: { name: string }) => {
      try {
        const segment = await emailMarketingApiService.createListSegment(companyId, body);
        setListSegments((current) => [...current, segment]);
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.segmentAdded" }));
        return segment;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const addNotificationContact = useCallback(
    async (companyId: string, body: { email: string; name?: string }) => {
      try {
        const contacts = await emailMarketingApiService.addNotificationContact(
          companyId,
          body
        );
        setListSetting((current) =>
          current ? { ...current, notificationContacts: contacts } : current
        );
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.api.notificationAdded" })
        );
        return contacts;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const removeNotificationContact = useCallback(
    async (companyId: string, email: string) => {
      try {
        const contacts = await emailMarketingApiService.removeNotificationContact(
          companyId,
          email
        );
        setListSetting((current) =>
          current ? { ...current, notificationContacts: contacts } : current
        );
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.api.notificationRemoved" })
        );
        return contacts;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const setupDomain = useCallback(
    async (companyId: string, domain: string) => {
      try {
        const dns = await emailMarketingApiService.setupDomain(companyId, domain);
        setConfig((current) =>
          current
            ? {
                ...current,
                domains: dns.domain
                  ? [
                      {
                        domain: dns.domain,
                        authenticated: Boolean(dns.authenticated),
                      },
                    ]
                  : [],
              }
            : current
        );
        toast.success(intl.formatMessage({ id: "app.emailMarketing.api.domainSaved" }));
        return dns;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  const repairMarketingSetup = useCallback(
    async (companyId: string) => {
      try {
        const result = await emailMarketingApiService.repairMarketingSetup(
          companyId
        );
        setConfig((current) =>
          current ? { ...current, delivery: result.delivery } : current
        );
        if (result.delivery.readyForBulk) {
          toast.success(
            intl.formatMessage({ id: "app.emailMarketing.delivery.ready" })
          );
        } else if (result.error) {
          toast.error(result.error);
        } else {
          toast.info(
            intl.formatMessage({ id: "app.emailMarketing.delivery.notReady" })
          );
        }
        return result;
      } catch (error) {
        console.error(error);
        toast.error(intl.formatMessage({ id: "app.toast.error13" }));
        return null;
      }
    },
    [intl]
  );

  return {
    overview,
    config,
    listSetting,
    subscribers,
    campaigns,
    templates,
    suppressions,
    customFields,
    listSegments,
    listTags,
    marketingLists,
    defaultListId,
    physicalAddressOptions,
    reports,
    loading,
    loadOverview,
    loadConfig,
    loadList,
    loadSubscribers,
    loadCampaigns,
    loadTemplates,
    getTemplate,
    loadSuppressions,
    loadReports,
    loadCustomFields,
    loadListSegments,
    loadListTags,
    loadMarketingLists,
    createMarketingList,
    createListTag,
    loadPhysicalAddressOptions,
    importSubscribers,
    importSubscribersBulk,
    deleteSubscriber,
    createSubscriber,
    createCampaign,
    createTemplate,
    updateCampaign,
    deleteCampaign,
    deleteCampaignsBulk,
    cancelScheduleCampaign,
    getCampaign,
    sendTest,
    scheduleCampaign,
    sendCampaignNow,
    updateList,
    createSender,
    createUnsubscribeGroup,
    loadDomainDns,
    setupDomain,
    repairMarketingSetup,
    createCustomField,
    createListSegment,
    addNotificationContact,
    removeNotificationContact,
  };
}
